/**
 * iSHIP operational modules — minimal PMS IA.
 *
 * Primary sidebar: Work Orders, Inventory, Procurement.
 * Tabs live inside each section workspace.
 * Legacy modules remain available via Explore / secondary rail.
 */

const tab = (id, name, description, extras = {}) => ({
  id,
  name,
  description,
  customPage: true,
  statuses: [],
  fields: [],
  ...extras,
});

export const ISHIP_MODULES = [
  {
    id: "dashboard",
    name: "Work Orders",
    icon: "Clipboard",
    sidebarName: "Work Orders",
    sidebarMode: "section",
    sidebarPrimary: true,
    workspaceNavigation: "sidebar",
    submodules: [
      tab(
        "overview",
        "Work Orders",
        "Fleet work-order status with vessel-wise PMS access"
      ),
    ],
  },
  {
    id: "maintenance",
    name: "Maintenance",
    icon: "Wrench",
    sidebarName: "Maintenance",
    sidebarMode: "section",
    sidebarPrimary: false,
    sidebarHidden: true,
    workspaceNavigation: "sidebar",
    submodules: [
      tab(
        "work-orders",
        "Work Orders",
        "WO lifecycle: Planned → Assigned → Ready → In Progress → Completed → Verification → Closed"
      ),
      {
        id: "plans",
        name: "Maintenance Plans",
        description: "Equipment + job plans, due board and automatic WO generation triggers",
        // Keep record API compatible with legacy maintenance/pms rows.
        apiModuleId: "maintenance",
        apiSubmoduleId: "pms",
        statuses: [
          "created",
          "in_progress",
          "completion_recorded",
          "authorized",
          "reschedule_accepted",
          "overdue",
          "deferred",
        ],
        fields: [
          { key: "job_code", label: "PMS job code", type: "text", required: true },
          { key: "equipment", label: "Equipment / system", type: "text", required: true },
          { key: "due_date", label: "Due date", type: "date", required: true },
          {
            key: "priority",
            label: "Priority",
            type: "select",
            options: ["Low", "Normal", "High", "Urgent"],
            required: true,
          },
          {
            key: "interval_type",
            label: "Interval",
            type: "select",
            options: ["Calendar", "Running hours", "Condition-based"],
          },
          { key: "running_hours", label: "Running hours (if any)", type: "number" },
          { key: "job_description", label: "Job description", type: "textarea", required: true },
        ],
      },
      tab("equipment", "Equipment", "Equipment hierarchy, counters, open work and spare readiness"),
      {
        id: "defects",
        name: "Defects & Findings",
        description: "Defects, failures, observations and inspection findings linked to work orders",
        hiddenTab: true,
        statuses: ["open", "in_progress", "completed"],
        fields: [
          { key: "reported_date", label: "Reported date", type: "date", required: true },
          { key: "code", label: "Defect code", type: "text", required: true },
          { key: "defect_title", label: "Defect title", type: "text", required: true },
          {
            key: "severity",
            label: "Severity",
            type: "select",
            options: ["minor", "major", "critical"],
            required: true,
          },
          {
            key: "source",
            label: "Source",
            type: "select",
            options: [
              "Weekly rounds",
              "Superintendent survey",
              "Class survey",
              "PSC",
              "Vetting (SIRE/RightShip)",
              "Insurance / H&M",
            ],
            required: true,
          },
          { key: "area", label: "Area / location", type: "text" },
          { key: "assigned_to", label: "Assigned to", type: "text" },
          { key: "deadline", label: "Deadline", type: "date" },
          { key: "description", label: "Description", type: "textarea", required: true },
        ],
      },
      tab("safety", "Safety", "JSA, permits, toolbox talks and isolations linked to work orders"),
      tab("certificates", "Certificates", "Vessel and equipment certificates with renewal work orders"),
      tab("history", "History", "Closed work, completions and equipment maintenance history"),
      tab("reports", "Reports", "PMS compliance, overdue, failure analysis and cost reports"),
      // Legacy aliases kept for redirects / old bookmarks (hidden from tabs).
      tab("pms", "Due Board (legacy)", "Redirects to Maintenance Plans", { hiddenTab: true }),
      tab("verification", "Verification (legacy)", "Redirects to Work Orders verification queue", {
        hiddenTab: true,
      }),
      tab("reports-analytics", "Reports (legacy)", "Redirects to Maintenance Reports", {
        hiddenTab: true,
      }),
      tab("hseq-inspections", "Inspections (legacy)", "Redirects to Defects & Findings", {
        hiddenTab: true,
      }),
    ],
  },
  {
    id: "inventory-procurement",
    name: "Inventory",
    icon: "Package",
    sidebarName: "Inventory",
    sidebarMode: "section",
    sidebarPrimary: true,
    workspaceNavigation: "sidebar",
    submodules: [
      {
        id: "stock",
        name: "Stock",
        description: "Onboard stock, minimums, critical spares",
        customPage: true,
        apiModuleId: "procurement",
        apiSubmoduleId: "inventory",
        statuses: ["in_stock", "below_minimum", "out_of_stock"],
        fields: [],
      },
      tab("reservations", "Reservations", "WO material reservations, shortages and issue status"),
      tab("history", "History", "Closed stock movements, receipts and issues"),
      tab("reports", "Reports", "Stock on hand, consumption, shortages and movement reports"),
      tab("material-readiness", "Material Readiness (legacy)", "Redirects to Reservations", {
        hiddenTab: true,
      }),
    ],
  },
  {
    id: "defect-list",
    name: "Defect list",
    icon: "WarningCircle",
    sidebarName: "Defect list",
    sidebarPrimary: false,
    sidebarHidden: true,
    submodules: [
      {
        id: "list",
        name: "Defect list",
        description: "Open defects, findings and failure reports across the fleet",
        // Persist against the existing maintenance/defects records.
        apiModuleId: "maintenance",
        apiSubmoduleId: "defects",
        statuses: ["open", "in_progress", "completed"],
        fields: [
          { key: "reported_date", label: "Reported date", type: "date", required: true },
          { key: "code", label: "Defect code", type: "text", required: true },
          { key: "defect_title", label: "Defect title", type: "text", required: true },
          {
            key: "severity",
            label: "Severity",
            type: "select",
            options: ["minor", "major", "critical"],
            required: true,
          },
          {
            key: "source",
            label: "Source",
            type: "select",
            options: [
              "Weekly rounds",
              "Superintendent survey",
              "Class survey",
              "PSC",
              "Vetting (SIRE/RightShip)",
              "Insurance / H&M",
            ],
            required: true,
          },
          { key: "area", label: "Area / location", type: "text" },
          { key: "assigned_to", label: "Assigned to", type: "text" },
          { key: "deadline", label: "Deadline", type: "date" },
          { key: "description", label: "Description", type: "textarea", required: true },
        ],
      },
    ],
  },

  /* ── Secondary operational modules (Explore / More) ── */
  {
    id: "crew-operations",
    name: "Crew operations",
    icon: "UsersThree",
    sidebarName: "Crew Ops",
    sidebarPrimary: false,
    workspaceNavigation: "sidebar",
    submodules: [
      {
        id: "weekly-task-management",
        name: "Weekly Planning",
        description:
          "Vessel-wise weekly plan report for Master, Superintendent, and Admin (view).",
        statuses: ["draft", "published", "in_progress", "completed", "locked"],
        fields: [
          { key: "week_start", label: "Week start (Monday)", type: "date", required: true },
          { key: "notes", label: "Week notes", type: "textarea" },
        ],
        departments: ["Deck", "Engine", "Catering", "Electrical"],
        days: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
        areas: [
          "Forecastle / Mooring",
          "Deck",
          "Cargo Holds (Hatch)",
          "Bridge",
          "Accommodation",
          "Steering Gear Room",
          "Engine Room",
        ],
        assignmentOptions: [
          "Master",
          "Chief Officer",
          "Second Officer",
          "Third Officer",
          "Bosun",
          "AB",
          "OS",
          "Chief Engineer",
          "Second Engineer",
          "Third Engineer",
          "Fitter / Motorman / Oiler",
          "ETO",
          "Chief Cook",
          "Steward / Messman",
        ],
        permitTypes: [
          "Hot Work Permit",
          "Enclosed Space Entry Permit",
          "Working Aloft Permit",
          "Electrical Isolation Permit",
          "Cold Work Permit",
          "General Permit to Work",
        ],
        taskPriorities: ["Routine", "Important", "Urgent", "Critical"],
        taskStatuses: ["planned", "in_progress", "completed", "cancelled", "blocked"],
        permitStatuses: ["not_required", "draft", "requested", "approved", "rejected", "closed"],
        approvalStatuses: ["NA", "Not Requested", "Pending", "Approved", "Rejected"],
        shoreStatuses: ["not_required", "requested", "confirmed", "declined"],
      },
      {
        id: "daily-tasks-register",
        name: "Daily Tasks Register",
        description:
          "Crew submit daily work; Master reviews; Super Admin / Superintendent see fleet consolidate",
        statuses: ["SUBMITTED", "APPROVED", "REJECTED"],
        fields: [
          { key: "task_date", label: "Date", type: "date", required: true },
          {
            key: "watch",
            label: "Watch / period",
            type: "select",
            options: ["0000-0400", "0400-0800", "0800-1200", "1200-1600", "1600-2000", "2000-2400", "Day work"],
          },
          { key: "location", label: "Location / space", type: "text" },
          { key: "task_description", label: "Task description", type: "textarea", required: true },
          { key: "performed_by", label: "Performed by", type: "text" },
        ],
      },
    ],
  },
  {
    id: "sms",
    name: "SMS",
    icon: "ShieldCheck",
    sidebarName: "SMS",
    sidebarPrimary: false,
    workspaceNavigation: "sidebar",
    submodules: [
      tab(
        "library",
        "SMS Library",
        "Controlled fleet SMS documents, vessel supplements, safety requirements and acknowledgements"
      ),
    ],
  },
  {
    id: "vessel-inspection",
    name: "Vessel Inspection",
    icon: "ClipboardText",
    sidebarName: "Inspection",
    sidebarPrimary: false,
    submodules: [
      {
        id: "vessel-inspection",
        name: "Inspections",
        description: "Vessel inspection planning & findings",
        statuses: ["planned", "in_progress", "findings_open", "closed"],
        fields: [
          { key: "inspection_date", label: "Inspection date", type: "date", required: true },
          {
            key: "inspection_type",
            label: "Type",
            type: "select",
            options: ["Internal", "Owner", "Class", "Flag", "PSC", "Vetting"],
            required: true,
          },
          { key: "inspector", label: "Inspector", type: "text" },
          { key: "port", label: "Port / location", type: "text" },
          { key: "scope", label: "Scope / areas", type: "textarea", required: true },
          { key: "findings_summary", label: "Findings summary", type: "textarea" },
        ],
      },
    ],
  },
  {
    id: "hseq",
    name: "HSEQ",
    icon: "FirstAidKit",
    sidebarName: "HSEQ",
    sidebarPrimary: false,
    submodules: [
      {
        id: "incidents",
        name: "Incidents",
        description: "Incident reporting & investigation",
        statuses: ["reported", "under_investigation", "capa_open", "closed"],
        fields: [
          { key: "incident_datetime", label: "Date / time", type: "datetime-local", required: true },
          {
            key: "incident_type",
            label: "Type",
            type: "select",
            options: ["Injury", "Near miss", "Environmental", "Security", "Property damage", "Other"],
            required: true,
          },
          { key: "location", label: "Location", type: "text" },
          { key: "severity_affected", label: "Persons affected", type: "number" },
          { key: "description", label: "Description", type: "textarea", required: true },
          { key: "immediate_action", label: "Immediate action", type: "textarea" },
        ],
      },
      {
        id: "safety-drills",
        name: "Safety drills",
        description: "SOLAS / company drill records",
        statuses: ["scheduled", "completed", "overdue"],
        fields: [
          { key: "drill_date", label: "Drill date", type: "date", required: true },
          {
            key: "drill_type",
            label: "Drill type",
            type: "select",
            options: ["Abandon ship", "Fire", "Man overboard", "Steering gear", "SOPEP", "Security", "Other"],
            required: true,
          },
          { key: "duration_min", label: "Duration (min)", type: "number" },
          { key: "participants", label: "Participants count", type: "number" },
          { key: "outcome", label: "Outcome / remarks", type: "textarea", required: true },
        ],
      },
    ],
  },
  {
    id: "drydock",
    name: "Drydock",
    icon: "Anchor",
    sidebarName: "Drydock",
    sidebarPrimary: false,
    submodules: [
      {
        id: "drydock-repair-list",
        name: "Project",
        description: "Drydock project workspace — planning, execution and close-out",
        statuses: ["planning", "active", "completed", "cancelled"],
        fields: [
          { key: "name", label: "Project name", type: "text", required: true },
          { key: "shipyard", label: "Shipyard", type: "text" },
          { key: "planned_start", label: "Planned start", type: "date" },
          { key: "planned_end", label: "Planned end", type: "date" },
          { key: "total_budget", label: "Total budget", type: "number" },
          { key: "notes", label: "Notes", type: "textarea" },
        ],
      },
      {
        id: "drydock-cost-tracking",
        name: "Cost tracking",
        description: "Budget vs actual for drydock",
        statuses: ["budgeted", "committed", "invoiced", "paid", "variance_review"],
        fields: [
          { key: "cost_date", label: "Date", type: "date", required: true },
          {
            key: "cost_category",
            label: "Category",
            type: "select",
            options: ["Steel", "Machinery", "Electrical", "Painting", "Docking dues", "Extras", "Other"],
            required: true,
          },
          { key: "budget_amount", label: "Budget", type: "number" },
          { key: "actual_amount", label: "Actual", type: "number", required: true },
          { key: "currency", label: "Currency", type: "select", options: ["USD", "EUR", "GBP", "SGD"] },
          { key: "remarks", label: "Remarks", type: "textarea" },
        ],
        hiddenTab: true,
      },
    ],
  },

  /* Legacy parents kept for URL redirects only */
  {
    id: "inventory",
    name: "Inventory",
    icon: "Package",
    sidebarName: "Inventory",
    sidebarPrimary: false,
    sidebarHidden: true,
    submodules: [
      {
        id: "stock",
        name: "Stock Overview",
        apiModuleId: "procurement",
        apiSubmoduleId: "inventory",
        customPage: true,
        statuses: [],
        fields: [],
      },
      tab("material-readiness", "Material Readiness", "Legacy"),
    ],
  },
  {
    id: "procurement",
    name: "Procurement",
    icon: "ShoppingCart",
    sidebarName: "Procurement",
    sidebarMode: "section",
    sidebarPrimary: true,
    workspaceNavigation: "sidebar",
    submodules: [
      {
        id: "requisition",
        name: "Requisitions",
        description: "Ship and shore purchase requests linked to work orders",
        statuses: ["draft", "submitted", "approved", "rejected", "ordered"],
        fields: [
          { key: "req_date", label: "Requisition date", type: "date", required: true },
          {
            key: "department",
            label: "Department",
            type: "select",
            options: ["Deck", "Engine", "Catering", "Office"],
            required: true,
          },
          { key: "item_description", label: "Item / service", type: "textarea", required: true },
          { key: "quantity", label: "Quantity", type: "number", required: true },
          { key: "unit", label: "Unit", type: "text" },
          { key: "needed_by", label: "Needed by", type: "date" },
        ],
      },
      tab("rfq", "RFQs", "Request for quotation tracker", { hiddenTab: true }),
      tab("quotations", "Quotations", "Vendor quotation comparison and award", {
        hiddenTab: true,
      }),
      tab("purchase-orders", "Purchase Orders", "PO lifecycle and delivery status", {
        hiddenTab: true,
      }),
      tab("goods-receipt", "Goods Receipt", "GRN, stock update and reservation fulfilment", {
        hiddenTab: true,
      }),
      tab("vendors", "Vendors", "Approved vendors, lead time and performance", {
        hiddenTab: true,
      }),
      tab("history", "History", "Closed requisitions, orders and receipts", {
        hiddenTab: true,
      }),
      tab("reports", "Reports", "Open requisitions, PO status, spend and vendor performance"),
      tab("pipeline", "Pipeline (legacy)", "Redirects to RFQs", { hiddenTab: true }),
      tab("rfq-quotes", "RFQ (legacy)", "Redirects to RFQs", { hiddenTab: true }),
      tab("delivery-note", "Delivery (legacy)", "Redirects to Goods Receipt", {
        hiddenTab: true,
      }),
      tab("inventory", "Inventory (legacy)", "Redirects to Inventory", { hiddenTab: true }),
    ],
  },
];

export const PMS_PRIMARY_MODULE_IDS = ["dashboard", "inventory-procurement", "procurement"];

export const findModule = (moduleId) => ISHIP_MODULES.find((m) => m.id === moduleId);

export const findSubmodule = (moduleId, submoduleId) => {
  const mod = findModule(moduleId);
  if (!mod) return null;
  return (mod.submodules || []).find((s) => s.id === submoduleId) || null;
};

/** Resolve the backend module/submodule ids used for iship_module_records. */
export const resolveModuleApiIds = (moduleId, submoduleId) => {
  const sub = findSubmodule(moduleId, submoduleId);
  return {
    apiModuleId: sub?.apiModuleId || moduleId,
    apiSubmoduleId: sub?.apiSubmoduleId || submoduleId,
  };
};

/** Submodules visible as workspace tabs for the current audience. */
export const visibleSubmodules = (moduleId, { isShoreAdmin = false } = {}) => {
  const mod = findModule(moduleId);
  if (!mod) return [];
  return (mod.submodules || []).filter((s) => {
    if (s.hiddenTab) return false;
    if (s.shoreOnly && !isShoreAdmin) return false;
    if (s.vesselOnly && isShoreAdmin) return false;
    return true;
  });
};

/**
 * Wrapper navigation policy.
 * `sidebar` means the module/submodule destinations are already represented in
 * the application rail and must not be repeated as a page-level tab strip.
 * Other modules retain their internal workflow/view tabs.
 */
export const shouldRenderWorkspaceTabs = (moduleId) =>
  findModule(moduleId)?.workspaceNavigation !== "sidebar";

export const modulePath = (moduleId, submoduleId) =>
  submoduleId ? `/modules/${moduleId}/${submoduleId}` : `/modules/${moduleId}`;

/** Dedicated vessel work-orders page (opened from Dashboard → Open WOs). */
export const vesselPmsPath = (vesselId) =>
  `/modules/maintenance/vessel-pms/${vesselId || "all"}`;

/** Modules shown as single section links in the sidebar. */
export const sidebarSectionModules = (modules = ISHIP_MODULES) =>
  modules.filter((m) => m.sidebarPrimary && !m.sidebarHidden);

/** Secondary modules (expanded submodule lists under More). */
export const sidebarSecondaryModules = (modules = ISHIP_MODULES) =>
  modules.filter((m) => !m.sidebarPrimary && !m.sidebarHidden);
